/**
 * V7 shared swipe-action hook — the ONE client path to POST /swipes for every
 * V7 surface (deck S-10, menu S-14, grids S-13: "acting from the grid
 * resolves the pair instantly" through the SAME endpoint).
 *
 * The client treats every verdict as a SERVER PROPOSAL (ARCH §2.3): the
 * server re-validates at write time, so this hook maps the normative error
 * surface — EC-22 cap (429 + reset_at), card-gone (404/409), cooldown /
 * restriction (403), SR-not-available (501) — into UI callbacks instead of
 * ever deciding locally.
 */
import { useCallback, useState } from "react";
import type { ApiComponents } from "@humble/shared";
import { resolveCopy } from "@humble/shared";
import { api, isApiError, withEventId } from "../../lib/api.js";
import { toast } from "../../design/index.js";

export type SwipeResult = ApiComponents["schemas"]["SwipeResult"];
export type Quotas = ApiComponents["schemas"]["Quotas"];
export type DeckCard = ApiComponents["schemas"]["DeckCard"];

export interface SwipeOutcome {
  kind: "resolved" | "gone" | "cap" | "interstitial" | "error";
  result?: SwipeResult;
  /** EC-22: reset instant for the cap card countdown. */
  resetAt?: string;
}

export interface SwipeVerdictInput {
  targetId: string;
  verdict: "like" | "reject" | "crush" | "super_reject";
  note?: string;
  sendAnyway?: boolean;
}

export function useSwipeAction(onQuotas?: (q: Quotas) => void) {
  const [busy, setBusy] = useState(false);

  const send = useCallback(
    async (input: SwipeVerdictInput): Promise<SwipeOutcome> => {
      setBusy(true);
      try {
        const body = withEventId<{
          client_event_id: string;
          target_id: string;
          verdict: SwipeVerdictInput["verdict"];
          note?: string;
          send_anyway?: boolean;
        }>({
          target_id: input.targetId,
          verdict: input.verdict,
          ...(input.note ? { note: input.note } : {}),
          ...(input.sendAnyway ? { send_anyway: true } : {}),
        });
        const result = (await api.post("/swipes", { body })) as SwipeResult;
        if (result.quotas && onQuotas) onQuotas(result.quotas);
        return { kind: "resolved", result };
      } catch (err) {
        if (isApiError(err)) {
          if (err.status === 429) {
            // EC-22: deck freezes into the cap card — no verdict recorded.
            return { kind: "cap", resetAt: err.resetAt };
          }
          if (err.code === "SEND_ANYWAY_REQUIRED") {
            return { kind: "interstitial" }; // #66b — caller re-confirms
          }
          if (err.code === "CARD_GONE" || err.code === "PAIR_CLOSED") {
            toast(resolveCopy("deck.card_gone") ?? "Card gone.", "info");
            return { kind: "gone" };
          }
          if (err.code === "COOLDOWN") {
            toast(err.message, "error");
            return { kind: "error" };
          }
          toast(err.message, "error");
          return { kind: "error" };
        }
        toast(resolveCopy("errors.offline") ?? "Offline.", "error");
        return { kind: "error" };
      } finally {
        setBusy(false);
      }
    },
    [onQuotas],
  );

  return { send, busy };
}

/** "Resets in {hh:mm}" — display-only; the server's reset_at is the truth. */
export function formatCountdown(toIso: string | undefined): string {
  if (!toIso) return "--:--";
  const ms = Math.max(0, new Date(toIso).getTime() - Date.now());
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** Signed-URL media route (V1 serves it; broken images degrade to initials). */
export function mediaUrl(mediaId: string | null | undefined): string | null {
  return mediaId ? `/api/v1/media/${mediaId}` : null;
}
